/**
 * In `@caresend/utils` we have a rad function called `fetchWithRelationships` that the type
 * `RequestWithRelationships` is used to define which various values are to be fetched for any
 * given object type defined under some `DbRef`.
 *
 * This is a way to use both client-side and cloud-side can utilize `getValueOnce` calls
 * in both (i) in series down the request stack and (ii) in parallel across sibling nodes
 * on the same object nesting level.
 *
 * (ex.)
 * Let's say we are trying to fetch an object of type Order and want to explicitly define
 * the children of the Order object to be fetched from their various `DbRef`s, and the children of
 * the children to be fetched from their various `DbRef`s.
 *
 * This is possible with the `fetchWithRelationships` function and enforced by the
 * `RequestWithRelationships` type which relies on values stored below in the `dbRefsWithRelationshipKeys`
 * object.
 *
 * Our request object could be defined as follows:
 * ```ts
 * const request = requestWithRelationships<DbRef.ORDERS>({
 *   createdBy: true,
 *   bookings: {
 *     procedures: {
 *       supplyItems: true,
 *       draftWaypoints: true,
 *     },
 *   },
 * });`
 * ```
 *
 * This will result in a request that will fetch the `createdBy` user and the `bookings` at the
 * order level, the `procedures` for each booking in the list of bookings, the `supplyItems` for
 * each procedure, and the `draftWaypoints` for each procedure and accordingly place them into a
 * structure that resembles the database schema.
 *
 * ```ts
 * {
 *   users: {
 *      ...
 *   },
 *   bookings: {
 *     ...
 *   },
 *   procedures: {
 *     ...
 *   },
 *   supplyItems: {
 *     ...
 *   },
 *   draftWaypoints: {
 *     ...
 *   },
 * }
 * ```
 *
 * Author: @pumposh 04/23/2024
*/
import { BookingRelationshipKeys } from "./booking";
import { DraftWaypointRelationshipKeys } from "./draftWaypoint";
import { DbRef } from "./firebase";
import { OfficeRelationshipKeys } from "./office";
import { OrderRelationshipKeys } from "./order";
import { PlaceGroupRelationshipKeys, PlaceGroupTypeRelationshipKeys, PlaceRelationshipKeys, } from "./places";
import { ProcedureRelationshipKeys } from "./procedures";
import { ShiftRelationshipKeys } from "./shift";
import { ShipmentRelationshipKeys, SupplyTransferRelationshipKeys, } from "./supply";
import { UserRelationshipKeys } from "./user";
import { ItineraryRelationshipKeys, WaypointActionRelationshipKeys, WaypointRelationshipKeys, } from "./waypoint";
/** This is where we define the actual relationship key structure for each object type. */
export const dbRefsWithRelationshipKeys = {
    /** Barcodes have no child IDs */
    [DbRef.BARCODES]: null,
    [DbRef.BOOKINGS]: BookingRelationshipKeys,
    [DbRef.DRAFT_WAYPOINTS]: DraftWaypointRelationshipKeys,
    [DbRef.ITINERARIES]: ItineraryRelationshipKeys,
    [DbRef.OFFICES]: OfficeRelationshipKeys,
    [DbRef.ORDERS]: OrderRelationshipKeys,
    [DbRef.PLACES]: PlaceRelationshipKeys,
    /** TODO: Define keys */
    [DbRef.PLACE_ACCOUNTS]: null,
    [DbRef.PLACE_GROUPS]: PlaceGroupRelationshipKeys,
    [DbRef.PLACE_GROUP_TYPES]: PlaceGroupTypeRelationshipKeys,
    [DbRef.PROCEDURES]: ProcedureRelationshipKeys,
    /** TODO: Define keys */
    [DbRef.PRODUCTS]: null,
    [DbRef.SHIFTS]: ShiftRelationshipKeys,
    [DbRef.SHIPMENTS]: ShipmentRelationshipKeys,
    /** Keys not supported for this structure */
    [DbRef.SUPPLY_INSTRUCTION_CONFIG_OPTIONS]: null,
    /** TODO: Define keys */
    [DbRef.SUPPLY_ITEMS]: null,
    [DbRef.SUPPLY_TRANSFERS]: SupplyTransferRelationshipKeys,
    [DbRef.USERS]: UserRelationshipKeys,
    [DbRef.WAYPOINTS]: WaypointRelationshipKeys,
    [DbRef.WAYPOINT_ACTIONS]: WaypointActionRelationshipKeys,
};
/**
 * Given the Dashboard Traverser queries all paths, a priority is defined for
 * returned data. The user can then enter a procedure ID, and get the procedure
 * first, then click the next button to get the associated supply instruction
 * config options which use the same ID.
 *
 * All refs from `RelationshipKeyDbRefTypes` must be included here to be
 * fetched from the Traverser on Dashboard.
 */
export const traverserDbRefsOrderedByPriority = [
    // Alphabetical
    DbRef.BOOKINGS,
    DbRef.DRAFT_WAYPOINTS,
    DbRef.ITINERARIES,
    DbRef.OFFICES,
    DbRef.ORDERS,
    DbRef.PLACES,
    DbRef.PLACE_ACCOUNTS,
    DbRef.PLACE_GROUPS,
    DbRef.PLACE_GROUP_TYPES,
    DbRef.PROCEDURES,
    DbRef.PRODUCTS,
    DbRef.SHIFTS,
    DbRef.SHIPMENTS,
    DbRef.SUPPLY_ITEMS,
    DbRef.SUPPLY_TRANSFERS,
    DbRef.USERS,
    DbRef.WAYPOINTS,
    DbRef.WAYPOINT_ACTIONS,
    // Lower priority
    DbRef.SUPPLY_INSTRUCTION_CONFIG_OPTIONS,
    DbRef.BARCODES,
];
//# sourceMappingURL=relationshipKeys.js.map